import 'focus-visible'
import '../src/styles/globals.scss'
import 'swiper/swiper.scss'
import 'swiper/components/pagination/pagination.scss'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { parseSitePhone } from 'app/utils'

import SwiperCore, { Pagination, A11y } from 'swiper'
import Head from 'next/head'
import { API, configPath } from 'app/apiService'
import { PagePropertiesProvider } from 'hooks/usePageProperies'
import { registerPageView } from 'app/tas'
import { googleTag, googleTagNoScript } from 'app/GTM'

import { init } from '../sentry'

init()

SwiperCore.use([Pagination, A11y])

const MyApp = ({ Component, pageProps, pageProperties, err }) => {
  if (Component.name === 'NotFound') {
    pageProperties.metaTitle = 'Page Not Found'
  }

  const { title, metaTitle, metaDescription = '' } = pageProperties

  useEffect(() => {
    registerPageView({ ...pageProperties })
  }, [])

  return (
    <PagePropertiesProvider pageProperties={pageProperties}>
      <Head>
        <title>{metaTitle || title}</title>
        <meta key="description" name="description" content={metaDescription} />
        <meta key="viewport" name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta key="msapplication-TileColor" name="msapplication-TileColor" content="#2d89ef" />
        <meta
          key="msapplication-config"
          name="msapplication-config"
          content="/static/favicon/browserconfig.xml"
        />
        <meta key="theme-color" name="theme-color" content="#ffffff" />
        <link
          key="apple-touch-icon"
          rel="apple-touch-icon"
          sizes="180x180"
          href="/static/favicon/apple-touch-icon.png"
        />
        <link
          key="icon32"
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/static/favicon/favicon-32x32.png"
        />
        <link
          key="icon16"
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/static/favicon/favicon-16x16.png"
        />
        <link key="manifest" rel="manifest" href="/static/favicon/site.webmanifest" />
        <link
          key="mask-icon"
          rel="mask-icon"
          href="/static/favicon/safari-pinned-tab.svg"
          color="#5252ff"
        />
        <link key="shortcut-icon" rel="shortcut icon" href="/static/favicon/favicon.ico" />
        <script key="gtm" dangerouslySetInnerHTML={{ __html: googleTag() }} />
      </Head>
      <noscript dangerouslySetInnerHTML={{ __html: googleTagNoScript() }} />
      <Component {...pageProps} err={err} />
    </PagePropertiesProvider>
  )
}

MyApp.getInitialProps = async ({ ctx }) => {
  const {
    req,
    query: { pageProperties = {} },
  } = ctx

  const { sitePhone, siteEmail, siteAddress, ...configs } = await API.get(
    configPath(['sitePhone', 'siteEmail', 'siteAddress'])
  )

  // Fall through to the next source whenever a value cannot produce a phone
  // number, so an unusable ?line= or a stale cookie never blanks the phone.
  const toPhone = (value) => parseSitePhone(parsePhoneNumberFromString, value)
  const parsedNumber =
    toPhone(req?.query?.line) || toPhone(pageProperties.cookies?.line) || toPhone(sitePhone)
  const line = {}
  if (parsedNumber) {
    line.sitePhone = parsedNumber
      .formatInternational()
      .replace(/[(,)+]/g, '')
      .replace(/ /g, '-')
    line.sitePhoneLink = parsedNumber.getURI()
  }

  return {
    pageProperties: {
      ...pageProperties,
      ...configs,
      ...line,
      siteEmail,
      siteAddress,
    },
  }
}

MyApp.propTypes = {
  Component: PropTypes.any,
  err: PropTypes.any,
  pageProps: PropTypes.object,
  pageProperties: PropTypes.object,
}

export default MyApp
